import {
  Button,
  Card,
  CardContent,
  Theme,
  Typography,
  WithStyles,
  createStyles,
  withStyles
} from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import autobind from 'autobind-decorator';
import * as React from 'react';
import { GenericLink } from 'src/components';
import { SnackbarVariant } from 'src/components/AppSnackbar';
import { API } from 'src/definitions';
import AxiosException from 'src/exceptions/AxiosException';
import ForgotPasswordRequest from 'src/schemas/ForgotPasswordRequest';
import RelmApi from 'src/services/RelmApi';
import { toast } from 'src/services/Toaster';
import ValidationTrait, {
  ValidationTraitState
} from 'src/services/ValidationTrait';
import { RelmIcon } from 'src/theme/icons';

// region component styles
const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    authCard: {
      margin: `0 ${theme.spacing()}px`,
      width: `${theme.spacing(50)}px`,
      height: `${theme.spacing(45)}px`,
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'space-between'
    },
    toolbarBrand: {
      display: 'flex',
      flexFlow: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      backgroundColor: theme.palette.primary.main,
      paddingTop: `${theme.spacing(1.5)}px`,
      paddingBottom: `${theme.spacing(1.5)}px`
    },
    toolbarBrandName: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      fontSize: `${theme.spacing(3.5)}px`
    },
    logo: {
      height: '46px',
      width: 'auto'
    },
    cardActionsRoot: {
      display: 'flex',
      flexFlow: 'column',
      alignItems: 'center',
      justifyContent: 'flex-end'
    },
    forgotPasswordButton: {
      marginBottom: `${theme.spacing()}px`
    }
  });

// endregion
// region component props
interface ExternalProps {
  classes?: Partial<ClassNameMap<keyof typeof styles>>;
}

type InternalProps = Required<ExternalProps>;

type Props = InternalProps & WithStyles<typeof styles>;

interface State
  extends ValidationTraitState<API.Authorization.ForgotPassword.Request> {
  disableSubmit: boolean;
}

// endregion

class ForgotPassword extends React.Component<Props, State> {
  static readonly defaultProps = {};

  private readonly _validator = new ValidationTrait(
    this,
    ForgotPasswordRequest
  );

  readonly state: State = {
    disableSubmit: false,
    inputValues: {
      email: ''
    },
    inputErrors: {
      email: ''
    }
  };

  async trySendForgotPasswordEmail() {
    try {
      await RelmApi.sendForgotPasswordEmail(this.state.inputValues.email);

      toast(
        SnackbarVariant.SUCCESS,
        `Password reset sent to ${this.state.inputValues.email}`
      );
    } catch (error) {
      if (error instanceof AxiosException) {
        toast(
          SnackbarVariant.ERROR,
          error.response!.data.message ||
            'Something went wrong trying to request a password reset'
        );

        return;
      }

      this._validator.handlePossibleValidationError(error);
      this.setState({ disableSubmit: false });

      return;
    }

    // clear the form
    this.setState({
      disableSubmit: false,
      inputValues: { email: '' }
    });
  }

  // region autobound methods
  @autobind
  handleForgotPasswordButtonClick() {
    this.setState({ disableSubmit: true });
    this.trySendForgotPasswordEmail();
  }

  // endregion
  // region render & get-render-content methods
  render() {
    return (
      <div className={this.props.classes.root}>
        <Card className={this.props.classes.authCard}>
          <CardContent className={this.props.classes.toolbarBrand}>
            <RelmIcon className={this.props.classes.logo} />
            <Typography
              variant="h4"
              className={this.props.classes.toolbarBrandName}
            >
              Forgot Password
            </Typography>
          </CardContent>
          <CardContent>
            {this._validator.buildTextFieldForProperty('email', 'Email', {
              required: true
            })}
          </CardContent>
          <CardContent className={this.props.classes.cardActionsRoot}>
            <Button
              className={this.props.classes.forgotPasswordButton}
              disabled={this.state.disableSubmit}
              fullWidth
              variant="outlined"
              onClick={this.handleForgotPasswordButtonClick}
            >
              Send reset link
            </Button>
            <Typography align="center" variant="body2">
              <GenericLink to="/signin">Remembered password?</GenericLink>
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  // endregion
}

export default withStyles(styles)(ForgotPassword);
